// Atletas: atributos, perfil posicional e geracao de elenco.
//
// Escala de atributo: 0..99, com 75 = jogador medio da liga (ver tuning.AVG).
// O motor de posse NUNCA le um atributo cru: le sempre a diferenca em relacao
// a 75. Isso e o que permite calibrar a liga inteira mexendo em `tuning.js`.

import { makeRng, clamp } from './rng.js';
import { makeUniqueName, NATIONALITIES } from './names.js';
import { AVG, ROTATION } from './tuning.js';

/** Posicoes, em ordem de armador a pivo. */
export const POSITIONS = ['ARM', 'AA', 'ALA', 'AP', 'PIV'];

export const POSITION_LABEL = {
  ARM: 'Armador',
  AA: 'Ala-armador',
  ALA: 'Ala',
  AP: 'Ala-pivo',
  PIV: 'Pivo',
};

export const ATTRIBUTES = [
  'twoShot',    // arremesso de 2 e finalizacao
  'threeShot',  // arremesso de 3
  'freeThrow',  // lance livre
  'handle',     // conducao / nao perder a bola
  'passing',    // assistencia
  'rebOff',     // rebote ofensivo
  'rebDef',     // rebote defensivo
  'defense',    // defesa geral
  'steal',      // roubo de bola
  'block',      // bloqueio
  'usage',      // tendencia de terminar a posse
  'threeTend',  // tendencia de escolher o arremesso de 3
  'stamina',    // resistencia (afeta minutos sustentaveis)
  'foulProne',  // propensao a cometer falta
];

// Deslocamento tipico de cada posicao em relacao ao atleta generico.
const POSITION_PROFILE = {
  ARM: { handle: 12, passing: 14, threeTend: 8, steal: 6, rebOff: -11, rebDef: -9, block: -19, twoShot: -3, foulProne: -5 },
  AA:  { handle: 5, passing: 3, threeShot: 6, threeTend: 11, steal: 3, rebOff: -7, rebDef: -5, block: -14, foulProne: -2 },
  ALA: { defense: 3, rebDef: 2, threeTend: 1, block: -4 },
  AP:  { rebOff: 8, rebDef: 9, block: 7, twoShot: 3, threeShot: -6, threeTend: -9, handle: -8, passing: -4, foulProne: 4 },
  PIV: { rebOff: 15, rebDef: 16, block: 17, twoShot: 8, threeShot: -15, threeTend: -23, handle: -15, passing: -6, foulProne: 7, freeThrow: -8 },
};

// Peso de cada atributo no overall, por posicao. Um pivo com 3 pontos ruim nao
// e um pivo ruim; um armador que nao passa, e.
const OVERALL_WEIGHTS = {
  ARM: { twoShot: 3, threeShot: 3, handle: 3, passing: 3, defense: 2, steal: 1, rebDef: 1, freeThrow: 1 },
  AA:  { twoShot: 3, threeShot: 4, handle: 2, passing: 2, defense: 2, steal: 1, rebDef: 1, freeThrow: 1 },
  ALA: { twoShot: 3, threeShot: 3, handle: 2, passing: 2, defense: 3, rebDef: 2, steal: 1, freeThrow: 1 },
  AP:  { twoShot: 3, threeShot: 2, rebOff: 2, rebDef: 3, defense: 3, block: 2, passing: 1, freeThrow: 1 },
  PIV: { twoShot: 4, rebOff: 3, rebDef: 3, defense: 3, block: 3, threeShot: 1, passing: 1, freeThrow: 1 },
};

/** Overall 0..99 derivado dos atributos, ponderado pela posicao. */
export function overallOf(player) {
  const w = OVERALL_WEIGHTS[player.position] || OVERALL_WEIGHTS.ALA;
  let sum = 0;
  let total = 0;
  for (const key in w) {
    sum += (player.attributes[key] ?? AVG) * w[key];
    total += w[key];
  }
  return Math.round(sum / total);
}

/**
 * Cria um atleta.
 * @param {object} o
 * @param {string} o.position   uma de POSITIONS
 * @param {number} o.talent     deslocamento de talento em pontos (0 = medio)
 * @param {string} o.nationality
 * @param {number} o.age
 * @param {string} o.seed       semente completa (clube|temporada|indice|posicao)
 * @param {Set<string>} o.takenNames
 */
export function makePlayer({ position, talent = 0, nationality = 'BR', age = 25, seed, takenNames = new Set() }) {
  const rng = makeRng(`atleta|${seed}`);
  const profile = POSITION_PROFILE[position] || {};
  const attributes = {};
  for (const attr of ATTRIBUTES) {
    const base = AVG + talent + (profile[attr] || 0);
    // Ruido individual: dois atletas do mesmo nivel nao sao iguais.
    const noise = rng.normal() * 5.5;
    attributes[attr] = Math.round(clamp(base + noise, 25, 99));
  }
  // Uso e uma consequencia do talento, nao um sorteio independente: quem joga
  // melhor termina mais posses.
  attributes.usage = Math.round(clamp(AVG + talent * 1.35 + rng.normal() * 4, 30, 99));

  const player = {
    id: `p${makeRng(`id|${seed}`)().toString(36).slice(2, 10)}`,
    name: makeUniqueName(takenNames, nationality, seed),
    position,
    nationality,
    age,
    attributes,
  };
  player.overall = overallOf(player);
  return player;
}

/**
 * Distribui os 240 minutos do jogo pelo elenco, na ordem de qualidade.
 * A soma e exatamente ROTATION.minutesPool -- se nao for, os minutos por
 * jogador de toda a liga saem errados e a calibracao nao fecha.
 */
export function assignTargetMinutes(roster, lineup = null) {
  // Minutagem escolhida pelo tecnico, quando existe. Guardada por ID e nunca
  // por indice: o elenco muda entre as partidas, e indice guardado vira o
  // atleta errado depois de uma venda.
  const escolhidos = lineup?.minutes;
  const order = [...roster].sort((a, b) => b.overall - a.overall);

  if (escolhidos && Object.keys(escolhidos).length) {
    const bruto = roster.map((p) => Math.max(0, Number(escolhidos[p.id] ?? 0)));
    const soma = bruto.reduce((a, b) => a + b, 0);
    if (soma > 0) {
      // Normaliza para os 240 minutos do jogo: o tecnico distribui a proporcao,
      // e o motor garante que a soma fecha. Sem isso, um elenco com 300 minutos
      // pedidos daria mais tempo de quadra do que a partida tem.
      const escala = ROTATION.minutesPool / soma;
      roster.forEach((p, i) => {
        p.targetMinutes = Math.round(bruto[i] * escala * 10) / 10;
      });
      return order;
    }
  }

  const curve = [...ROTATION.starterCurve, ...ROTATION.benchCurve];
  const raw = order.map((_, i) => curve[i] ?? 0);
  const sum = raw.reduce((a, b) => a + b, 0) || 1;
  const scale = ROTATION.minutesPool / sum;
  order.forEach((player, i) => {
    player.targetMinutes = Math.round(raw[i] * scale * 10) / 10;
  });
  return order;
}

/** Minutagem automatica sugerida, para a tela mostrar como ponto de partida. */
export function suggestedMinutes(roster) {
  const clone = roster.map((p) => ({ ...p }));
  assignTargetMinutes(clone);
  const out = {};
  for (const p of clone) out[p.id] = p.targetMinutes;
  return out;
}

/**
 * Elenco de 15 atletas com distribuicao posicional realista e um pico de
 * talento (o "astro"), controlado por `strength` (-1 = pior time da liga,
 * +1 = melhor).
 */
export function makeRoster({
  teamId, season = 1, strength = 0, nationality = 'BR', takenNames = new Set(),
  level = AVG, size = 13, spread = 1,
}) {
  const rng = makeRng(`elenco|${teamId}|${season}`);
  // 13 atletas: 3 de cada posicao de fora, 2 de cada posicao de dentro. Cobre
  // foul-out e lesao sem buraco posicional, e deixa 2 vagas livres ate o teto
  // de 15 -- sem elas o usuario so poderia comprar depois de vender, e o
  // mercado ficaria travado no primeiro dia de carreira.
  const slots = [];
  for (const pos of POSITIONS) {
    const count = pos === 'AP' || pos === 'PIV' ? 2 : 3;
    for (let d = 0; d < count; d++) slots.push(pos);
  }
  while (slots.length > size) slots.pop();
  while (slots.length < size) slots.push(POSITIONS[slots.length % POSITIONS.length]);

  // Embaralha os SLOTS antes de aplicar a curva de talento. Sem isso o degrau
  // mais alto da curva cai sempre no primeiro slot, e o astro de todos os 30
  // times da liga seria armador.
  for (let i = slots.length - 1; i > 0; i--) {
    const j = rng.int(i + 1);
    [slots[i], slots[j]] = [slots[j], slots[i]];
  }

  // Curva de talento dentro do elenco: astro, titulares, banco, fim de banco.
  const talentCurve = [14, 9, 6, 4, 2, 0, -2, -4, -6, -8, -11, -14, -18];

  const roster = slots.map((position, i) => {
    // Embaralha qual posicao recebe qual degrau da curva, senao o astro seria
    // sempre armador em todos os times da liga.
    // `level` e o nivel medio da LIGA: e o que faz um elenco do NBB ser mais
    // fraco (e mais barato) que um da NBA sem nenhuma regra especial.
    // `spread` comprime a curva em ligas menores: o melhor atleta do NBB e
    // muito melhor que o pior do NBB, mas nao e um astro da NBA.
    const talent = (talentCurve[i] ?? -20) * spread + (level - AVG)
      + strength * 4.5 * spread + rng.normal() * 1.5;
    const nat = rng.chance(0.72) ? nationality : NATIONALITIES[rng.int(NATIONALITIES.length)];
    const age = Math.round(clamp(19 + rng.normal() * 3.6 + 6, 19, 39));
    return makePlayer({
      position,
      talent,
      nationality: nat,
      age,
      seed: `${teamId}|${season}|${i}|${position}`,
      takenNames,
    });
  });

  assignTargetMinutes(roster);
  return roster;
}
